import { sodApi } from '@/services/index'

type ExampleHeaders = {
  mimeType?: string
  token?: string
}

export const getHeaders = (headers: ExampleHeaders) => {
  if (!headers) {
    throw new Error('Headers object is missing')
  }

  const resultHeaders: Record<string, string> = {}

  const headerMappings: Record<string, string> = {
    mimeType: 'Accept',
    token: 'Authorization',
  }

  for (const key in headerMappings) {
    const typedKey = key as keyof ExampleHeaders
    if (headers[typedKey]) {
      resultHeaders[headerMappings[key]] = headers[typedKey]!
    }
  }

  return resultHeaders
}
// Define a service using a base URL and expected endpoints
export const AnnotateApi = sodApi.injectEndpoints({
  endpoints: (builder) => ({
    getEvidenceFile: builder.query({
      query: (evidenceId) => ({
        headers: getHeaders({ mimeType: 'application/octet-stream' }),
        url: `/Evidence/${evidenceId}/File`,
        method: 'GET',
        responseHandler: async (response) => {
          const blob = await response.blob()
          return blob
        },
      }),
    }),
    getAnnotationType: builder.query<any, any>({
      query: (evidenceType) => ({
        url: `/Annotation/types/${evidenceType}`,
        method: 'GET',
      }),
    }),
    createAnnotation: builder.mutation<any, any>({
      query: (body) => ({
        url: `/Annotation/Create`,
        method: 'POST',
        body,
      }),
    }),
    getJobStatus: builder.query<any, any>({
      query: (jobId) => ({
        url: `/JobRecord/${jobId}/status`,
        method: 'GET',
      }),
    }),
    getExtractedText: builder.query<any, any>({
      query: (annotateId) => ({
        url: `/Annotation/${annotateId}/EvaluatedText`,
        method: 'GET',
      }),
    }),
    cancelAnnotate: builder.query<any, any>({
      query: (evidenceId) => ({
        url: `/Evidence/${evidenceId}/CancelAnnotations`,
        method: 'GET',
      }),
    }),
    verifyAnnotate: builder.mutation<any, any>({
      query: ({ evidenceId, body, draft }) => ({
        url: `/Evidence/${evidenceId}/VerifyAnnotations`,
        method: 'POST',
        body,
        params: draft ? { draft: true } : undefined,
      }),
    }),
    deleteAnnotation: builder.query<any, any>({
      query: (annotateId) => ({
        url: `/Annotation/${annotateId}/Delete`,
        method: 'GET',
      }),
    }),
    confirmNoSystemAccount: builder.mutation<any, any>({
      query: (annotationId) => ({
        url: `/Evidence/ConfirmNoSystemAccount/${annotationId}`,
        method: 'POST',
      }),
    }),
    getAnnotationList: builder.query<any, any>({
      query: (evidenceId) => ({
        url: `/Annotation/list/${evidenceId}`,
        method: 'GET',
      }),
    }),
    getAnnotationJobStatuses: builder.query<{ jobId: string; statusId: number; status: string }[], void>({
      query: () => ({ url: `/JobRecord/annotation-jobs`, method: 'GET' }),
    }),
  }),
})
// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLazyGetEvidenceFileQuery,
  useGetAnnotationTypeQuery,
  useCreateAnnotationMutation,
  useLazyGetJobStatusQuery,
  useGetJobStatusQuery,
  useLazyGetExtractedTextQuery,
  useLazyCancelAnnotateQuery,
  useVerifyAnnotateMutation,
  useLazyDeleteAnnotationQuery,
  useConfirmNoSystemAccountMutation,
  useGetAnnotationListQuery,
  useLazyGetAnnotationListQuery,
  useGetAnnotationJobStatusesQuery,
  useLazyGetAnnotationJobStatusesQuery,
} = AnnotateApi
